import PropTypes from 'prop-types';
import React from 'react';
import { AlertTriangle, Badge, InfoCircle } from '@opendate-app/setlist';

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const wholeMoney = (value) => (
  value % 1 === 0
    ? money.format(value).replace(/\.00$/, '')
    : money.format(value)
);

const splitShortfallShape = PropTypes.shape({
  amount: PropTypes.number.isRequired,
  unreachable: PropTypes.bool.isRequired,
  splitPoint: PropTypes.number,
  grossPotential: PropTypes.number,
  guarantee: PropTypes.number
});

const shortfallLabel = (shortfall) => (
  shortfall.unreachable
    ? `Unreachable — short ${money.format(shortfall.amount)}`
    : `Below split by ${money.format(shortfall.amount)}`
);

export const SplitShortfallPill = ({ shortfall }) => (
  <div style={{ marginTop: '4px' }} data-testid="split-shortfall-pill">
    <Badge
      type="badge-color"
      color="warning"
      size="sm"
      icon={shortfall.unreachable ? AlertTriangle : InfoCircle}
    >
      {shortfallLabel(shortfall)}
    </Badge>
  </div>
);

SplitShortfallPill.propTypes = { shortfall: splitShortfallShape.isRequired };

export const SplitPointCaption = () => (
  <div style={{ marginTop: '2px' }}>
    <Badge type="pill-outline" color="warning" size="sm">Above gross potential</Badge>
  </div>
);

const calloutStyle = {
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-start',
  padding: '10px 12px',
  borderRadius: '8px',
  background: 'var(--sl-bg-warning-primary)',
  border: '1px solid var(--sl-border-warning)',
  textAlign: 'left'
};

const calloutIconStyle = {
  display: 'inline-flex',
  flex: 'none',
  marginTop: '2px',
  color: 'var(--sl-fg-warning)'
};

const calloutTitleStyle = {
  fontSize: '13px',
  fontWeight: '700',
  lineHeight: '18px',
  color: 'var(--setlist-warning-700)'
};

const calloutEyebrowStyle = {
  fontSize: '11px',
  fontWeight: '700',
  lineHeight: '16px',
  letterSpacing: '.06em',
  color: 'var(--setlist-warning-700)'
};

const calloutBodyStyle = {
  fontSize: '13px',
  fontWeight: '400',
  lineHeight: '18px',
  color: 'var(--setlist-warning-800)'
};

export const SplitShortfallCallout = ({ shortfall }) => (
  <div style={calloutStyle} role="status" data-testid="split-shortfall-callout">
    <span style={calloutIconStyle}><AlertTriangle width={14} height={14} aria-hidden="true" /></span>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={calloutEyebrowStyle}>INTERNAL NOTE ONLY</span>
      <span style={calloutTitleStyle}>
        {shortfall.unreachable
          ? 'Split pays $0 — unreachable'
          : `Split pays $0 — below split by ${money.format(shortfall.amount)}`}
      </span>
      <span style={calloutBodyStyle}>
        {shortfall.unreachable
          ? `Split point ${wholeMoney(shortfall.splitPoint)} is above the ${wholeMoney(shortfall.grossPotential)} gross potential. Guarantee ${wholeMoney(shortfall.guarantee)} stands.`
          : `Show net doesn't cover the ${wholeMoney(shortfall.guarantee)} guarantee.`}
      </span>
    </div>
  </div>
);

SplitShortfallCallout.propTypes = { shortfall: splitShortfallShape.isRequired };
